import { useParams, useNavigate } from 'react-router-dom';
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/claim-detail/ require four levels up to reach convex/_generated/
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TClaimStatus } from '@/@types/claims';
import { useClaimData } from './useClaimData';
import { useClaimDetailActions } from './useClaimDetailActions';
import { ClaimInfoCard } from './ClaimInfoCard';
import { ClaimDocumentsPanel } from './ClaimDocumentsPanel';
import { ClaimLineItemRow } from './ClaimLineItemRow';
import { UtilizationPanel } from './UtilizationPanel';
import { STATUS_BADGE, STATUS_BADGE_VARIANT, STATUS_TRANSITIONS } from './claimDetailUtils';
import type { TLineItem, TBenefitItem, TUtilization, TDocument } from './claimDetailTypes';

export function ClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // AS_JUSTIFIED: one-time Id cast from route params
  const claimId = id as Id<'claims'>;

  const { claimData, benefitItems, utilization } = useClaimData(claimId);

  const claimStatus = claimData?.status as TClaimStatus | undefined; // AS_JUSTIFIED: Convex return status string narrowed to TClaimStatus for STATUS_TRANSITIONS lookup
  const nextStatuses = (claimStatus && STATUS_TRANSITIONS[claimStatus]) ?? [];

  const {
    notes,
    setNotes,
    isUpdatingStatus,
    isSavingNotes,
    handleStatusChange,
    handleSaveNotes,
    handleUpdateLineItem,
    handleRecordPayment,
  } = useClaimDetailActions({
    claimId,
    claimStatus,
    internalNotes: claimData?.internalNotes,
  });

  if (claimData === undefined || benefitItems === undefined || utilization === undefined) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (claimData === null) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Claim not found.</p>
        <Button variant="ghost" className="mt-3" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-border bg-card px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-foreground"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">{claimData.claimNumber}</h1>
          {claimStatus && (
            <Badge
              variant={STATUS_BADGE_VARIANT[claimStatus]}
              className={cn('text-xs font-medium capitalize', STATUS_BADGE[claimStatus])}
            >
              {claimStatus.replace(/_/g, ' ')}
            </Badge>
          )}
        </div>
        <Select
          value=""
          onValueChange={handleStatusChange}
          disabled={isUpdatingStatus || nextStatuses.length === 0}
        >
          <SelectTrigger className="w-48 h-8 text-sm border-border cursor-pointer">
            <SelectValue placeholder="Change status…" />
          </SelectTrigger>
          <SelectContent>
            {nextStatuses.map(s => (
              <SelectItem key={s} value={s} className="capitalize">
                Move to {s.replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-6 px-6 pb-8">
        <ClaimInfoCard
          claim={claimData}
          claimApplicationData={
            claimData.claimApplicationData as
              | import('@/@types/claims').IClaimApplicationData
              | undefined
          } // AS_JUSTIFIED: claimApplicationData shape matches IClaimApplicationData from wizard
        />

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">Line Items</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {claimData.lineItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No line items on this claim.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Description</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Claimed</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Approved</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Paid</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Status</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Benefit Item</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {claimData.lineItems.map((item: TLineItem) => (
                    <ClaimLineItemRow
                      key={item._id}
                      item={item as TLineItem} // AS_JUSTIFIED: Convex return narrowed to local TLineItem type matching return validator shape
                      currency={claimData.currency}
                      benefitItems={benefitItems as TBenefitItem[]} // AS_JUSTIFIED: Convex return narrowed to local TBenefitItem[] matching return validator shape
                      utilization={utilization as TUtilization[]} // AS_JUSTIFIED: Convex return narrowed to local TUtilization[] matching return validator shape
                      onUpdateLineItem={handleUpdateLineItem}
                      onRecordPayment={handleRecordPayment}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* AS_JUSTIFIED: Convex return narrowed to local TDocument[] */}
        <ClaimDocumentsPanel documents={claimData.documents as TDocument[]} />

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">Internal Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Visible to org members only — not shown to the claimant.
            </p>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add internal notes about this claim…"
              rows={4}
            />
            <Button size="sm" onClick={handleSaveNotes} disabled={isSavingNotes}>
              {isSavingNotes ? 'Saving…' : 'Save Notes'}
            </Button>
          </CardContent>
        </Card>

        <UtilizationPanel
          utilization={utilization as TUtilization[]} // AS_JUSTIFIED: Convex return narrowed to local TUtilization[] matching return validator shape
          benefitItems={benefitItems as TBenefitItem[]} // AS_JUSTIFIED: Convex return narrowed to local TBenefitItem[] matching return validator shape
          currency={claimData.currency}
        />
      </div>
    </div>
  );
}
